
import ChosenTags from "../components/ChosenTags.vue";
import Filters from "../components/Filters.vue";
import PreSelectedFilters from "../components/PreSelectedFilters.vue";
import SortOrderDropDown from "../components/SortOrderDropDown.vue";
import ResultsIndicator from "../components/ResultsIndicator.vue";
import ResultsReturned from "../components/ResultsReturned.vue";
import ResultItemsContainer from "../components/ResultItemsContainer.vue";
import RenderResources from "../components/renderless/RenderResources.vue";
import RenderSettings from "../components/renderless/RenderSettings.vue";
import { defineComponent, Ref, ref, watch, onBeforeMount } from "vue";
import {
  paginationClick,
  lastPage,
  firstPage,
  totalPages,
  currentActivePage,
} from "../common/pagination";
import {
  resourcesChanged,
  clearKeyword,
  updateKeyword,
  keywordInput,
  keyword,
} from "../common/searching";
import {
  updateTags,
  clearTag,
  clearAllTags,
  tags,
  selectedTags,
} from "../common/tags";
import { QueryString } from "../models/queryString";
import { parseQueryString } from "../common/urlHelpers";
import { SelectedCtaModel } from "../clients/search/src/models";
import { SortOrderOption } from "../models/sortOrderOptions";
import vClickOutside from "click-outside-vue3";
import "../extensions/string.extensions";

export default defineComponent({
  directives: {
    clickOutside: vClickOutside.directive,
  },
  components: {
    Filters,
    PreSelectedFilters,
    SortOrderDropDown,
    ResultsIndicator,
    ResultsReturned,
    RenderResources,
    ChosenTags,
    ResultItemsContainer,
    RenderSettings,
  },
  props: {
    showkeywords: {
      type: Boolean,
      required: true,
      default: true,
    },
    filtergroupids: {
      type: String,
      required: true,
      default: "",
    },
    filteryear: {
      type: Boolean,
      required: true,
      default: true,
    },
    parentid: {
      type: Number,
      required: true,
      default: -1,
    },
    pagesize: {
      type: Number,
      required: true,
      default: 20,
    },
    title: {
      type: String,
      required: true,
      default: "",
    },
    resultType: {
      type: String,
      required: true,
      default: "",
    },
    baseUri: {
      type: String,
      required: true,
      default: "",
    },
    language: {
      type: String,
      required: false,
      default: "",
    },
    personalisationGroupIds: {
      type: String,
      required: false,
      default: "",
    },
  },

  setup(props) {
    const apiendpoint = `${props.baseUri}`;
    const sortOrder = ref("score");
    const sortType = ref("Score");
    const panelContent: Ref<HTMLElement | null> = ref(null);
    const panelButton: Ref<HTMLElement | null> = ref(null);
    const sortIsDesc = ref(false);
    const images = [
      "previewImage",
      "previewImageRollover",
      "previewImageCutout",
    ];
    const additionalFields: string[] = [];
    const additionalFieldsMultiLingual: string[] = [];
    const language = props.language;
    const personalisationGroupIds = props.personalisationGroupIds;
    const keywordError = ref(false);
    const preselectedFilterItem: Ref<SelectedCtaModel | undefined> = ref(
      undefined
    );
    const currentSelectedTags: Ref<{
      categoryId: string;
      count?: number;
      id: string;
      title: string;
    }[]> = ref([]);

    if (
      props.resultType === "ResultNews" ||
      props.resultType === "ResultEvents"
    ) {
      additionalFields.push("datestamp");
      additionalFields.push("displayDate");
      if (props.resultType === "ResultNews") {
        additionalFieldsMultiLingual.push("previewTagHeader");
      }
      if (props.resultType === "ResultEvents") {
        additionalFields.push("eventLocation");
      }
    }

    onBeforeMount(() => {
      const qsParams: QueryString = parseQueryString();
      for (const paramKey in qsParams) {
        if (paramKey === "keyword") {
          keyword.value = qsParams[paramKey].toString();
          updateKeyword();
        }
      }
    });

    const updateSortData = (newVal: SortOrderOption) => {
      sortOrder.value = newVal.key;
      sortType.value = newVal.type;
      sortIsDesc.value = newVal.isDescending;
    };

    const keywordLengthOK = () =>
      keyword && (keyword.value.length === 0 || keyword.value.length > 2);

    const trySearch = () => {
      const keywordOK = keywordLengthOK();
      keywordError.value = !keywordOK;
      if (keywordOK) {
        updateKeyword();
      }
    };

    const onClickOutsideFilterPanel = (event) => {
      if (panelButton?.value) {
        panelButton.value.classList.remove("panelActive");
        panelButton.value.setAttribute("aria-expanded", "false");
      }

      if (panelContent?.value) {
        panelContent.value.setAttribute("aria-hidden", "true");
        panelContent.value.setAttribute("aria-expanded", "false");
        panelContent.value.classList.remove("panelActive");
      }

      document.body.classList.remove("darkOverlay");
      document.documentElement.classList.remove("stopScroll");
    };

    const filterSidebarClick = (event) => {
      event.stopPropagation();
      currentSelectedTags.value = tags.value;
      const panelButton = event.target;

      panelButton.classList.add("panelActive");
      panelButton.setAttribute("aria-expanded", "true");

      if (panelContent?.value) {
        panelContent.value.setAttribute("aria-hidden", "false");
        panelContent.value.setAttribute("aria-expanded", "true");
        panelContent.value.classList.add("panelActive");
      }

      document.body.classList.add("darkOverlay");
      document.documentElement.classList.add("stopScroll");
    };

    const filterSidebarCloseClick = (event) => {
      currentSelectedTags.value = tags.value;
      const panelButton = event.target;

      panelButton.classList.remove("panelActive");
      panelButton.setAttribute("aria-expanded", "false");

      if (panelContent?.value) {
        panelContent.value.setAttribute("aria-hidden", "true");
        panelContent.value.setAttribute("aria-expanded", "false");
        panelContent.value.classList.remove("panelActive");
      }

      document.body.classList.remove("darkOverlay");
      document.documentElement.classList.remove("stopScroll");
    };

    const clickFilters = (
      tagId: number,
      preselctedFilterList: SelectedCtaModel[]
    ) => {
      preselectedFilterItem.value = preselctedFilterList.find(
        (psf) => psf.tagId === tagId
      );
      clearAllTags();
      updateTags([
        {
          categoryId: preselectedFilterItem.value.categoryId.toString(),
          count: undefined,
          id: preselectedFilterItem.value.tagId.toString(),
          title: preselectedFilterItem.value.tagName,
        },
      ]);
    };

    const updateCurrentSelectedTags = (
      updatedTagList: {
        categoryId: string;
        count?: number;
        id: string;
        title: string;
      }[]
    ) => {
      currentSelectedTags.value = updatedTagList;
    };

    const applyFilters = ($event) => {
      trySearch();
      if (keywordError.value) {
        $event.stopImmediatePropagation();
        $event.preventDefault();
        return;
      }
       const panelButton = event.target;
 
      if (panelContent?.value) {
        panelContent.value.setAttribute("aria-hidden", "true");
        panelContent.value.setAttribute("aria-expanded", "false");
        panelContent.value.classList.remove("panelActive");
      }

      document.body.classList.remove("darkOverlay");
      document.documentElement.classList.remove("stopScroll");
      updateTags(currentSelectedTags.value);
    };

    const lastFocusTab = () => {
      if (panelButton?.value) {
        panelButton.value.classList.remove("panelActive");
        panelButton.value.setAttribute("aria-expanded", "false");
      }

      if (panelContent?.value) {
        panelContent.value.setAttribute("aria-hidden", "true");
        panelContent.value.setAttribute("aria-expanded", "false");
        panelContent.value.classList.remove("panelActive");
      }

      document.body.classList.remove("darkOverlay");
      document.documentElement.classList.remove("stopScroll");
    };

    const panelEsc = () => {
      if (panelButton?.value) {
        panelButton.value.classList.remove("panelActive");
        panelButton.value.setAttribute("aria-expanded", "false");
      }

      if (panelContent?.value) {
        panelContent.value.setAttribute("aria-hidden", "true");
        panelContent.value.setAttribute("aria-expanded", "false");
        panelContent.value.classList.remove("panelActive");
      }

      document.body.classList.remove("darkOverlay");
      document.documentElement.classList.remove("stopScroll");
    };

    watch(
      () => keyword.value,
      (newVal) => {
        if (newVal.length === 0 || newVal.length > 2) {
          keywordError.value = false;
        }
      }
    );

    return {
      resourcesChanged,
      clearKeyword,
      updateKeyword,
      updateTags,
      paginationClick,
      lastPage,
      clearTag,
      clearAllTags,
      firstPage,
      updateSortData,
      keywordLengthOK,
      trySearch,
      clickFilters,
      filterSidebarClick,
      filterSidebarCloseClick,
      onClickOutsideFilterPanel,
      updateCurrentSelectedTags,
      applyFilters,
      currentSelectedTags,
      apiendpoint,
      tags,
      totalPages,
      selectedTags,
      currentActivePage,
      keywordInput,
      keyword,
      keywordError,
      images,
      additionalFields,
      additionalFieldsMultiLingual,
      sortOrder,
      sortType,
      panelContent,
      panelButton,
      sortIsDesc,
      language,
      preselectedFilterItem,
      personalisationGroupIds,
      lastFocusTab,
      panelEsc,
    };
  },
});
